import type { IResponse } from "@/features/api/extranet"
import type {
  IdccPerimetreDTO,
  DemandeRattachementDTO,
  DemandeValidationCode,
  InscriptionResultat,
  EntiteAdherentDTO, EntitePrestaireDTO
} from '@/features/api/extranet/dto/Rattachements'
import { useFetch } from "@/features/fetch"
import { ensureSuccessStatusCode } from "@/features/fetch/ensureSuccessStatusCode"
import type { ContextAppType } from '@/features/auth/context-app'

const INIT_DEFAULT: RequestInit = { sablier: true, dialogMode: 'retry' } as const

export function buildSearch<T extends ContextAppType>(path: T) {

  return async function (search: string, init?: RequestInit) {
    init = {
      ...INIT_DEFAULT,
      ...init,
    }

    const url = `/inscriptions/${path}?search=${search}`
    const fetch = useFetch('extranet')
    const response = await fetch(url, init)
    ensureSuccessStatusCode(response)

    type TEntite = T extends 'adherent' ? EntiteAdherentDTO : EntitePrestaireDTO
    const result = (await response.json()) as IResponse<TEntite[]>

    return result.data
  }
}

export async function searchIdccPerimetre(search: string, codePostal?: string, init?: RequestInit): Promise<IdccPerimetreDTO[]> {
  search = search.trim().toLowerCase()

  const listIdccPerimetre = await loadIdccPerimetre(codePostal, init)

  const searchIntitulé = RegExp(`${search}`, 'i')
  return listIdccPerimetre.filter(idcc => idcc.code.includes(search) || searchIntitulé.test(idcc.intitule ?? ''))
}

async function loadIdccPerimetre(codePostal?: string, init?: RequestInit): Promise<IdccPerimetreDTO[]> {
  init = {
    appCache: true,
    ...INIT_DEFAULT,
    ...init,
  }

  const fetch = useFetch('extranet')
  const url = codePostal ? `/inscriptions/idcc?codepostal=${codePostal}` : '/inscriptions/idcc'
  const response = await fetch(url, init)
  ensureSuccessStatusCode(response)

  const result = (await response.json()) as IResponse<IdccPerimetreDTO[]>

  return result.data
}

export function buildPostDemandeRattachement<T extends DemandeRattachementDTO>(path: 'adherent' | 'prestataire') {

  return async function (demande: T, init?: RequestInit) {
    init = {
      ...INIT_DEFAULT,
      ...init,
      body: JSON.stringify(demande),
      method: "POST",
    }

    const fetch = useFetch('extranet')
    const response = await fetch(`/inscriptions/${path}`, init)
    ensureSuccessStatusCode(response)

    const result = (await response.json()) as IResponse<InscriptionResultat>
    return result.data
  }
}

export async function postValidationCode(demande: DemandeValidationCode, init?: RequestInit) {
  init = {
    ...INIT_DEFAULT,
    ...init,
    dialogExcludeErrors: [404],
    body: JSON.stringify(demande),
    method: "POST",
  }

  const fetch = useFetch('extranet')
  const response = await fetch('/inscriptions/code', init)

  if (response.status === 404) return false

  ensureSuccessStatusCode(response)
  return true
}

